import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { 
  Box, 
  Typography, 
  Button,
  Grid,
  TextField,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Select,
  Autocomplete,
  useTheme,
  Card,
  CardContent,
  Divider,
  Modal,
  IconButton,
  FormLabel,
  FormHelperText
} from '@mui/material';
import { 
  School as SchoolIcon,
  Upload as UploadIcon,
  ContactMail as ContactIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import PlaceSelect from '../../../Components/PlaceSelect/PlaceSelect';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { MuiTelInput } from 'mui-tel-input';
import { createAxiosInstance, API_URL } from '../../../api';
import axios from 'axios';
import Swal from 'sweetalert2';
import dayjs from 'dayjs';
import successIcon from "../../../assets/success-icon.svg";
import errorIcon from "../../../assets/error-icon.svg";
import CourseBilling from '../../CourseBilling/CourseBilling';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import UploadCourseImage from '../../AddCourse/Components/UploadCourseImage';
import { getCourseImageDisplayUrlWithCleanup } from '../../../utils/imageUtils';

const stripePublicKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
const stripePromise = loadStripe(stripePublicKey);

const CreateCourseForm = ({ courseId }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const userId = useSelector((state) => state.main.userId);
  const token = useSelector((state) => state.main.token);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState({});
  const [isOngoing, setIsOngoing] = useState(false);
  const [isFree, setIsFree] = useState(false);
  const [showAddress, setShowAddress] = useState(false);
  const [editorData, setEditorData] = useState("");
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [createdCourse, setCreatedCourse] = useState(null);
  const [createdCourseId, setCreatedCourseId] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [imageDisplayData, setImageDisplayData] = useState({ url: '/assets/course-place1.png', cleanup: () => {} });
  const [isEditMode, setIsEditMode] = useState(false);
  const [courseLoading, setCourseLoading] = useState(false);
  const [postcodeError, setPostcodeError] = useState(null);
  const [userHasPaid, setUserHasPaid] = useState(false);

  const loadCourseData = async () => {
    try {
      setCourseLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_SERVER}/api/courses/${courseId}`);
      const courseData = response.data;
      // Check if courseData is an array (some APIs return arrays)
      const actualCourseData = Array.isArray(courseData) ? courseData[0] : courseData;
      
      // Update form state with course data (following old flow pattern)
      const { date, time, ...filteredCourseData } = actualCourseData;
      
      // Check if course is ongoing (handle multiple variations)
      const courseIsOngoing = actualCourseData.isOngoing === 'true' || 
                              actualCourseData.isOngoing === true ||
                              actualCourseData.isongoing === 'true' ||
                              actualCourseData.isongoing === true ||
                              actualCourseData.isOngoing === 'on-going' ||
                              actualCourseData.isongoing === 'on-going';
      
      // Handle date conversion using dayjs (like old flow)
      // For ongoing courses, end date should always be null
      const processedDate = date ? [
        date[0] ? dayjs(date[0]) : null,
        courseIsOngoing ? null : (date[1] ? dayjs(date[1]) : null) // Always null for ongoing courses
      ] : [];
      
      // Handle contactSources - check if it's string or array
      let contactSourcesArray = [];
      if (actualCourseData.contactSources) {
        if (typeof actualCourseData.contactSources === 'string') {
          // If it's a string, split by comma
          contactSourcesArray = actualCourseData.contactSources.split(',').map(s => s.trim());
        } else if (Array.isArray(actualCourseData.contactSources)) {
          // If it's an array, check if it contains strings or comma-separated strings
          if (actualCourseData.contactSources.length === 1 && actualCourseData.contactSources[0].includes(',')) {
            // Single element with comma-separated values
            contactSourcesArray = actualCourseData.contactSources[0].split(',').map(s => s.trim());
          } else {
            // Multiple elements or single element without commas
            contactSourcesArray = actualCourseData.contactSources;
          }
        }
      }
      
      // Map "message" to "our-message-system" to match old flow
      const mappedContactSources = contactSourcesArray.map(source => 
        source === 'message' ? 'our-message-system' : source
      );

      // Handle types - API returns array, ensure it's properly handled
      const typesArray = actualCourseData.types || [];

      console.log('About to call setCourse with:', {
        ...filteredCourseData,
        date: processedDate,
        time: time || '',
        types: typesArray,
        inPerson: typesArray.includes('in-person'),
        online: typesArray.includes('online'),
        contactSources: mappedContactSources,
      });

      // Handle time conversion - API returns string, convert to Date object for dayjs
      const processedTime = time ? new Date(`2000-01-01T${time}`) : null;

      setCourse(prevCourse => ({
        ...prevCourse,
        ...filteredCourseData,
        date: processedDate,
        time: processedTime,
        // Handle types array - API returns ['online'] or ['in-person'] or both
        types: typesArray,
        inPerson: typesArray.includes('in-person'),
        online: typesArray.includes('online'),
        // Handle contactSources - convert string to array and map values
        contactSources: mappedContactSources,
      }));

      // Handle free course logic (like old flow)
      if (actualCourseData.coursePrice === "Free" || !actualCourseData.coursePrice || actualCourseData.coursePrice === "0") {
        setIsFree(true);
      } else {
        setIsFree(false);
      }
      
      // Set ongoing state (handle multiple variations from API)
      setIsOngoing(courseIsOngoing);
      setEditorData(actualCourseData.description || '');

      // Set separate state variables for dropdowns and form controls (like old flow)
      if (actualCourseData.subject) {
        setSelectedSubject(actualCourseData.subject);
        setSubject(actualCourseData.subject);
      }
      if (actualCourseData.level) {
        setSelectedLevel(actualCourseData.level);
        setLevel(actualCourseData.level);
      }

      console.log('Course state updated with:', {
        name: actualCourseData.name || '',
        subject: actualCourseData.subject || '',
        level: actualCourseData.level || '',
        coursePrice: actualCourseData.coursePrice || '',
        description: actualCourseData.description || '',
        types: actualCourseData.types || [],
        contactSources: actualCourseData.contactSources,
        mappedContactSources: mappedContactSources,
        isOngoing: actualCourseData.isOngoing,
        date: actualCourseData.date || [],
        telephone: actualCourseData.telephone || '',
        time: actualCourseData.time || '',
        processedTime: processedTime
      });

      // Handle image
      if (actualCourseData.image) {
        const imageUrl = getCourseImageDisplayUrlWithCleanup(actualCourseData.image);
        setImageDisplayData(imageUrl);
      }

    } catch (error) {
      console.error('Error loading course data:', error);
      setError({ general: 'Failed to load course data' });
    } finally {
      setCourseLoading(false);
    }
  };

  // Duration options matching the old form
  const durationOptions = [
    {
      key: "1",
      value: "£4.99",
    },
    {
      key: "2", 
      value: "£7.99",
    },
    {
      key: "6",
      value: "£12.99",
    },
    {
      key: "12",
      value: "£19.99",
    },
  ];
  
  // Subjects and levels
  const [subjects, setSubjects] = useState([]);
  const [filteredSubjects, setFilteredSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [subject, setSubject] = useState("");
  const [availableLevels, setAvailableLevels] = useState([]);
  const [selectedLevel, setSelectedLevel] = useState("");
  const [level, setLevel] = useState("");
  
  // Course state
  const [course, setCourse] = useState({
    name: "",
    subject: "",
    level: "",
    date: [],
    time: "",
    duration: "Duration of Advertising",
    advPrice: "",
    addOne: "",
    addTwo: "",
    postCode: "",
    town: "",
    city: "",
    telephone: "",
    description: "",
    image: "",
    coursePrice: "",
    file: "",
    email: "",
    socialLink: "",
    by: "",
    types: [],
    contactSources: [],
    userId: userId,
    isOngoing: "",
  });

  // Debug course state changes
  useEffect(() => {
    console.log('Course state changed:', course);
  }, [course]);

  // Check user payment status when in edit mode
  useEffect(() => {
    const checkUserPaymentStatus = async () => {
      if (isEditMode && userId) {
        try {
          const response = await createAxiosInstance().post(
            `${API_URL}/api/billings/haspaid/${userId}`
          );
          const paidStatus = response.data?.paid;
          // User has paid if status is "Paid" (includes trial users)
          setUserHasPaid(paidStatus === "Paid" || paidStatus === true);
        } catch (error) {
          console.error('Error checking payment status:', error);
          setUserHasPaid(false);
        }
      }
    };
    
    checkUserPaymentStatus();
  }, [isEditMode, userId]);

  // Load course data if editing
  useEffect(() => {
    if (courseId) {
      console.log('Edit mode activated for courseId:', courseId);
      setIsEditMode(true);
      loadCourseData();
    }
  }, [courseId]);

  // Effect to set available levels when both subjects and course data are loaded
  useEffect(() => {
    if (subjects.length > 0 && selectedSubject) {
      const subjectObj = subjects.find(s => s.name === selectedSubject);
      if (subjectObj) {
        setAvailableLevels(subjectObj.levels || []);
      }
    }
  }, [subjects, selectedSubject]);

  // Sync level state with course state (like old flow)
  useEffect(() => {
    setCourse((prevCourse) => ({
      ...prevCourse,
      level: level,
    }));
  }, [level]);

  // Handle image display data cleanup
  useEffect(() => {
    return () => {
      imageDisplayData.cleanup();
    };
  }, [imageDisplayData]);

  useEffect(() => {
    if (course?.image) {
      const displayData = getCourseImageDisplayUrlWithCleanup(course.image);
      setImageDisplayData(displayData);
    } else {
      setImageDisplayData({ url: '/assets/course-place1.png', cleanup: () => {} });
    }
  }, [course?.image]);

  const debounceTimeoutRef = useRef(null);

  // Levels options
  const levels = [
    { value: "primary-7-11", key: "Primary (7/11+)" },
    { value: "primary-5-11", key: "Primary (5-11 yrs)" },
    { value: "secondary-11-14", key: "Secondary (11-14 yrs)" },
    { value: "gcse-15-16", key: "GCSE (15-16 yrs)" },
    { value: "a-level-5-18", key: "A-level (15-18 yrs)" },
    { value: "degree-adults", key: "Degree/Adults" },
    { value: "other", key: "Other" },
  ];

  // Advertising duration options
  const price = [
    { key: "1", value: "£4.99" },
    { key: "2", value: "£7.99" },
    { key: "6", value: "£12.99" },
    { key: "12", value: "£19.99" },
  ];

  // Fetch subjects on component mount
  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_SERVER}/api/subjects?limit=1000`);
        setSubjects(response.data.subjects);
        setFilteredSubjects(response.data.subjects);
      } catch (error) {
        console.error("Error fetching subjects:", error);
      }
    };
    fetchSubjects();
  }, []);

  // Handle subject input change with debouncing
  const handleSubjectInputChange = async (value) => {
    if (value.length === 0) {
      setFilteredSubjects(subjects);
      setSelectedLevel("");
    } else if (value.length > 2) {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = setTimeout(async () => {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_SERVER}/api/subjects?search=${value}`
          );
          const subjectsData = response.data.subjects;
          setFilteredSubjects(subjectsData);
        } catch (error) {
          console.error("Error fetching filtered subjects:", error);
        }
      }, 300);
    }
  };

  // Handle checkbox changes
  const handlePlaceSelect = (event) => {
    const place = event.target.value;
    if (!place) return;
    setPostcodeError(place.error || null);
    setCourse((prev) => ({
      ...prev,
      postCode: place.formatted_address || place.postcode || '',
      town: place.town || place.city || '',
      latitude: place.latitude || -1,
      longitude: place.longitude || -1,
      address: place.formatted_address || '',
    }));
  };

  const handleCheckboxChange = (event) => {
    const { value } = event.target;
    setCourse((prevCourse) => {
      const updatedTypes = prevCourse.types.includes(value)
        ? prevCourse.types.filter((type) => type !== value)
        : [...prevCourse.types, value];
      
      const shouldShowAddress = updatedTypes.includes("in-person");
      setShowAddress(shouldShowAddress);
      
      return { ...prevCourse, types: updatedTypes };
    });
  };

  const handleContactCheckboxChange = (event) => {
    const { value } = event.target;
    setCourse((prevCourse) => {
      const updatedContactSources = prevCourse.contactSources.includes(value)
        ? prevCourse.contactSources.filter((source) => source !== value)
        : [...prevCourse.contactSources, value];
      
      return { ...prevCourse, contactSources: updatedContactSources };
    });
  };

  // Handle form submission - Create course first, then process payment
  const handleSubmit = async (e) => {
    console.log('course', course);
    e.preventDefault();
    let newError = {};

    // Comprehensive validation
    if (course.name.trim().length === 0) newError.name = "Please input Course Name.";
    if (!course.subject || course.subject.trim().length === 0)
      newError.subject = "Please select a Subject.";
    if (!course.level || course.level.trim().length === 0)
      newError.level = "Please select a Level.";
    if ((!course?.date?.length || course.date.length === 0) && !isOngoing)
      newError.date = "Please input Course Date or Select OnGoing";
    if (!course.types || course.types.length === 0)
      newError.types = "Please select at least one course type (In-person or Online).";
    if (!course.image) newError.image = "Please upload an image.";
    if (course.advPrice.trim().length === 0) newError.advPrice = "Please Select a Package.";
    if (!isFree && (!course.coursePrice || course.coursePrice.trim().length === 0))
      newError.coursePrice = "Please enter a course price or mark as free.";
    if (course.types.includes("in-person") && course.addOne.length === 0)
      newError.address = "Please Enter Address";
    if (course.contactSources.includes("email") && course.email.trim().length === 0)
      newError.email = "Please Enter Email";
    if (course.contactSources.includes("phone") && course.telephone.trim().length === 0)
      newError.telephone = "Please Enter Telephone";
    if (course.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(course.email)) {
        newError.email = "Please enter a valid email address.";
      }
    }
    
    if (course.contactSources.length === 0)
      newError.contactSources = "Please select a contact source.";

    
    setError(newError);
    if (Object.keys(newError).length > 0) {
      Swal.fire({
        imageUrl: errorIcon,
        imageHeight: "100px",
        title: "Please complete all required fields",
        text: "Please review the form and fill in all highlighted fields above.",
        confirmButtonColor: "#712d94",
      });
      return;
    }
    
    // For edit mode, check if user has paid (not course approval status)
    if (isEditMode) {
      // Only show payment modal if user has NOT paid
      // User payment status is checked via hasPaid API (includes trial users)
      if (userHasPaid) {
        // User has paid (or on trial) - update without payment
        try {
          await updateCourseAfterPayment();
        } catch (error) {
          // Error is already handled in updateCourseAfterPayment
          console.error('Error updating course:', error);
        }
        return;
      } else {
        // User has not paid - show payment modal
        setIsPaymentModalOpen(true);
        return;
      }
    }
    
    // For new course: Create course FIRST, then process payment
    setLoading(true);
    try {
      // Prepare form data
      const formData = new FormData();
      
      // Handle file uploads - backend expects arrays
      if (course.image && typeof course.image !== "string") {
        formData.append("image", course.image);
      }
      if (course.file && typeof course.file !== "string") {
        formData.append("file", course.file);
      }

      // Prepare course data
      // For ongoing courses, ensure end date is always null
      const formattedDate = course.date ? course.date.map((d, index) => {
        if (isOngoing && index === 1) return null; // Always null for ongoing end date
        if (!d) return null;
        const dateObj = dayjs(d);
        return dateObj.isValid() ? dateObj.format('YYYY-MM-DD') : null;
      }) : [];
      
      const courseData = {
        ...course,
        userId: userId,
        isOngoing: isOngoing ? "on-going" : "",
        coursePrice: isFree ? "0" : course.coursePrice,
        description: editorData || course.description,
        date: formattedDate,
        time: course.time ? dayjs(course.time).format('HH:mm') : "",
        types: course.types.join(','),
        contactSources: course.contactSources.join(','),
        // Remove file objects from course data
        image: undefined,
        file: undefined
      };
      formData.append("courseData", JSON.stringify(courseData));

      // Create course first (before payment)
      const response = await createAxiosInstance().post(
        `${API_URL}/api/courses`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      const createdCourseId = response.data._id || response.data.id;
      
      // Store courseId for payment processing
      setCreatedCourseId(createdCourseId);
      
      // Now show payment modal
      setIsPaymentModalOpen(true);
    } catch (error) {
      console.error('Error creating course:', error);
      Swal.fire({
        imageUrl: errorIcon,
        imageHeight: "100px",
        title: "Oops...",
        text: error?.response?.data?.message || "Your course could not be created, please try again!",
        confirmButtonColor: "#712d94",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle course update after payment (for edit mode)
  const updateCourseAfterPayment = async () => {
    setLoading(true);
    try {
      // Prepare form data
      const formData = new FormData();
      
      // Handle file uploads - backend expects arrays
      if (course.image && typeof course.image !== "string") {
        formData.append("image", course.image);
      }
      if (course.file && typeof course.file !== "string") {
        formData.append("file", course.file);
      }

      // Prepare course data
      // For ongoing courses, ensure end date is always null
      const formattedDate = course.date ? course.date.map((d, index) => {
        if (isOngoing && index === 1) return null; // Always null for ongoing end date
        if (!d) return null;
        const dateObj = dayjs(d);
        return dateObj.isValid() ? dateObj.format('YYYY-MM-DD') : null;
      }) : [];
      
      const courseData = {
        ...course,
        userId: userId,
        isOngoing: isOngoing ? "on-going" : "",
        coursePrice: isFree ? "0" : course.coursePrice,
        description: editorData || course.description,
        date: formattedDate,
        time: course.time ? dayjs(course.time).format('HH:mm') : "",
        types: course.types.join(','),
        contactSources: course.contactSources.join(','),
        // Remove file objects from course data
        image: undefined,
        file: undefined
      };
      formData.append("courseData", JSON.stringify(courseData));

      // Update existing course
      await createAxiosInstance().put(
        `${API_URL}/api/courses/edit/${courseId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      setIsPaymentModalOpen(false);
      
      Swal.fire({
        imageUrl: successIcon,
        imageHeight: "100px",
        title: "Course Updated Successfully!",
        html: "Your course has been updated successfully.",
        width: 600,
        confirmButtonColor: "#712d94",
        customClass: {
          icon: "no-before-icon",
        },
      }).then(() => {
        navigate("/my-courses");
      });

    } catch (error) {
      console.error('Error updating course:', error);
      setIsPaymentModalOpen(false);
      
      Swal.fire({
        imageUrl: errorIcon,
        imageHeight: "100px",
        title: "Oops...",
        text: error?.response?.data?.message || "Your course could not be updated, please try again!",
        confirmButtonColor: "#712d94",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle payment success for new course - Course already created, just show success message
  const handlePaymentSuccess = async () => {
    setCreatedCourseId(null);
    setIsPaymentModalOpen(false);
    
    Swal.fire({
      imageUrl: successIcon,
      imageHeight: "100px",
      title: "Thank you for your payment.",
      html: "Your advert will appear once this has been approved by admin team (within 24 hours). You will also receive an email.",
      width: 600,
      confirmButtonColor: "#712d94",
      customClass: {
        icon: "no-before-icon",
      },
    }).then(() => {
      navigate("/my-courses");
    });
  };

  const handlePaymentFailure = async () => {
    await deleteUnpaidCourse();
  };

  const deleteUnpaidCourse = async () => {
    if (createdCourseId && !isEditMode) {
      try {
        // Delete the course that was created before payment
        await createAxiosInstance().delete(
          `${API_URL}/api/courses/${createdCourseId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        console.log('Course deleted - payment not completed');
        setCreatedCourseId(null);
      } catch (deleteError) {
        console.error('Error deleting unpaid course:', deleteError);
        // Log but don't show error to user
      }
    }
  };

  // Handle modal close - delete course if payment wasn't completed
  const handlePaymentModalClose = () => {
    // Only delete if course was created but payment hasn't succeeded
    // Check if payment was successful by seeing if we still have createdCourseId
    if (createdCourseId && !isEditMode) {
      deleteUnpaidCourse();
    }
    setIsPaymentModalOpen(false);
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
      <Grid container spacing={3}>
        {/* Left Column - Basic Information */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SchoolIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Course Information
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Course Name */}
                <TextField
                  fullWidth
                  label="Name of course/class"
                  value={course.name}
                  onChange={(e) => setCourse(prev => ({ ...prev, name: e.target.value }))}
                  error={!!error.name}
                  helperText={error.name}
                />

                {/* Subject and Level */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Autocomplete
                    fullWidth
                    value={selectedSubject}
                    onInputChange={(_, value) => handleSubjectInputChange(value)}
                    onChange={(_, value) => {
                      setSelectedSubject(value || "");
                      setCourse((prev) => ({ ...prev, subject: value || "" }));
                      const selected = subjects.find((s) => s.name === value) || filteredSubjects.find((s) => s.name === value);
                      setAvailableLevels(selected?.levels || []);
                      setSelectedLevel("");
                      // Clear subject error when subject is selected
                      if (value && error.subject) {
                        setError(prev => ({ ...prev, subject: undefined }));
                      }
                    }}
                    options={filteredSubjects.map((s) => s.name)}
                    filterOptions={(options, { inputValue }) => {
                      const term = inputValue.toLowerCase();
                      return options.filter((option) => {
                        const lower = option.toLowerCase();
                        if (lower.includes(term)) return true;
                        return lower.replace(/[^a-z0-9 ]/g, '').includes(term);
                      });
                    }}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Subject" 
                        error={!!error.subject}
                        helperText={error.subject}
                      />
                    )}
                  />
                  
                  <TextField
                    fullWidth
                    select
                    label="Level"
                    value={selectedLevel}
                    onChange={(e) => {
                      setSelectedLevel(e.target.value);
                      setCourse(prev => ({ ...prev, level: e.target.value }));
                      // Clear level error when level is selected
                      if (e.target.value && error.level) {
                        setError(prev => ({ ...prev, level: undefined }));
                      }
                    }}
                    disabled={availableLevels.length === 0}
                    error={!!error.level}
                    helperText={error.level}
                  >
                    {availableLevels.length === 0 ? (
                      <MenuItem value="Not needed">Not Needed</MenuItem>
                    ) : (
                      availableLevels.map((level) => (
                        <MenuItem key={level} value={level}>
                          {level}
                        </MenuItem>
                      ))
                    )}
                  </TextField>
                </Box>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  {/* Dates */}
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <DatePicker
                      label="Start Date"
                      value={course.date && course.date[0] ? dayjs(course.date[0]) : null}
                      maxDate={course.date && course.date[1] ? dayjs(course.date[1]) : undefined}
                      onChange={(newValue) => {
                        const startDate = newValue ? newValue.toDate() : null;
                        const endDate = course.date && course.date[1] ? course.date[1] : null;
                        setCourse(prev => ({ ...prev, date: [startDate, endDate] }));
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!error.date,
                          helperText: error.date
                        }
                      }}
                      sx={{ flex: 1, minWidth: '200px' }}
                    />
                    <DatePicker
                      label={isOngoing ? "On Going" : "End Date"}
                      value={course.date && course.date[1] ? dayjs(course.date[1]) : null}
                      minDate={course.date && course.date[0] ? dayjs(course.date[0]) : undefined}
                      onChange={(newValue) => {
                        const startDate = course.date && course.date[0] ? course.date[0] : null;
                        const endDate = newValue ? newValue.toDate() : null;
                        setCourse(prev => ({ ...prev, date: [startDate, endDate] }));
                      }}
                      disabled={isOngoing}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!error.date,
                          helperText: error.date
                        }
                      }}
                      sx={{ flex: 1, minWidth: '200px' }}
                    />
                  </Box>

                  {/* On-going checkbox */}
                  <FormControl>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isOngoing}
                            onChange={(e) => {
                              setIsOngoing(e.target.checked);
                              if (e.target.checked) {
                                setCourse(prev => ({ 
                                  ...prev, 
                                  isOngoing: "on-going",
                                  date: prev.date ? [prev.date[0], null] : [null, null] // Set end date to null
                                }));
                              } else {
                                setCourse(prev => ({ ...prev, isOngoing: "" }));
                              }
                            }}
                          />
                        }
                        label="On-going"
                      />
                    </FormGroup>
                  </FormControl>

                  {/* Time */}
                  <DesktopTimePicker
                    label="Time of Course"
                    value={course.time ? dayjs(course.time) : null}
                    onChange={(newValue) => {
                      if (newValue) {
                        setCourse(prev => ({ ...prev, time: newValue.toDate() }));
                      } else {
                        setCourse(prev => ({ ...prev, time: null }));
                      }
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!error.time,
                        helperText: error.time,
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: '#712d94',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#712d94',
                            },
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            color: '#712d94',
                          },
                        }
                      },
                      popper: {
                        sx: {
                          '& .MuiClock-pin': {
                            backgroundColor: '#712d94',
                          },
                          '& .MuiClockPointer-root': {
                            backgroundColor: '#712d94',
                          },
                          '& .MuiClockPointer-thumb': {
                            backgroundColor: '#712d94',
                            borderColor: '#712d94',
                          },
                          '& .MuiClockNumber-root.Mui-selected': {
                            backgroundColor: '#712d94',
                            color: '#ffffff',
                          },
                          '& .MuiClock-clock': {
                            backgroundColor: 'rgba(113, 45, 148, 0.05)',
                          },
                        }
                      }
                    }}
                    sx={{ width: '100%', mb: 3 }}
                  />
                </LocalizationProvider>

                {/* Course Type */}
                <FormControl component="fieldset" error={!!error.types}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Course Type
                  </Typography>
                  <FormGroup row>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={course?.types?.includes("in-person")}
                          onChange={handleCheckboxChange}
                          value="in-person"
                        />
                      }
                      label="In-Person"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={course?.types?.includes("online")}
                          onChange={handleCheckboxChange}
                          value="online"
                        />
                      }
                      label="Online"
                    />
                  </FormGroup>
                  {error.types && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                      {error.types}
                    </Typography>
                  )}
                </FormControl>

                {/* Address (if in-person) */}
                {showAddress && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      required
                      fullWidth
                      label="Address Line 1"
                      value={course.addOne}
                      onChange={(e) => setCourse(prev => ({ ...prev, addOne: e.target.value }))}
                      error={!!error.address}
                      helperText={error.address}
                    />
                    <TextField
                      fullWidth
                      label="Address Line 2"
                      value={course.addTwo}
                      onChange={(e) => setCourse(prev => ({ ...prev, addTwo: e.target.value }))}
                    />
                    <FormControl fullWidth>
                      <FormLabel
                        id="postcode-label"
                        sx={{ mb: 1, fontWeight: 600, fontSize: "1rem" }}
                      >
                        Postcode (UK only) <span style={{ color: "red" }}>*</span>
                      </FormLabel>
                      <PlaceSelect
                        value={course.postCode}
                        onChange={handlePlaceSelect}
                        error={postcodeError}
                      />
                      <FormHelperText error={!!postcodeError} sx={{ mt: 1 }}>
                        {postcodeError ||
                          "Your full address will never be shown. Only area e.g. London, Muswell Hill"}
                      </FormHelperText>
                    </FormControl>
                  </Box>
                )}

                {/* Course Price */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <TextField
                      fullWidth
                      label="Course Price"
                      type="number"
                      value={course.coursePrice}
                      onChange={(e) => setCourse(prev => ({ ...prev, coursePrice: e.target.value }))}
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 1 }}>£</Typography>
                      }}
                      disabled={isFree}
                      error={!!error.coursePrice}
                      helperText={error.coursePrice}
                    />
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      minWidth: 'fit-content',
                      pt: 1.5 // Align with TextField
                    }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isFree}
                            onChange={(e) => {
                              setIsFree(e.target.checked);
                              if (e.target.checked) {
                                setCourse(prev => ({ ...prev, coursePrice: "0" }));
                              }
                            }}
                          />
                        }
                        label="Free"
                        sx={{ 
                          margin: 0,
                          '& .MuiFormControlLabel-label': {
                            fontSize: '0.875rem',
                            whiteSpace: 'nowrap'
                          }
                        }}
                      />
                    </Box>
                  </Box>
                </Box>

                {/* Advertising Duration */}
                <TextField
                  required
                  fullWidth
                  select
                  label="Duration of Advertising"
                  value={course.advPrice}
                  onChange={(e) => {
                    const selectedOption = durationOptions.find(opt => opt.value === e.target.value);
                    setCourse({ 
                      ...course, 
                      advPrice: selectedOption.value,
                      duration: selectedOption.key
                    });
                  }}
                  error={!!error.advPrice}
                  helperText={error.advPrice}
                  SelectProps={{
                    renderValue: (value) => {
                      const option = durationOptions.find(opt => opt.value === value);
                      if (!option) return value;
                      const numberWords = {
                        "1": "One", "2": "Two", "3": "Three", "4": "Four", "5": "Five",
                        "6": "Six", "7": "Seven", "8": "Eight", "9": "Nine", "10": "Ten",
                        "11": "Eleven", "12": "Twelve"
                      };
                      const durationText = option.key === "1" 
                        ? `${numberWords[option.key]} Month` 
                        : option.key === "12" 
                        ? "One Year" 
                        : `${numberWords[option.key]} Months`;
                      return `${option.value} (${durationText})`;
                    }
                  }}
                >
                  {durationOptions.map((option) => {
                    const numberWords = {
                      "1": "One", "2": "Two", "3": "Three", "4": "Four", "5": "Five",
                      "6": "Six", "7": "Seven", "8": "Eight", "9": "Nine", "10": "Ten",
                      "11": "Eleven", "12": "Twelve"
                    };
                    const durationText = option.key === "1" 
                      ? `${numberWords[option.key]} Month` 
                      : option.key === "12" 
                      ? "One Year" 
                      : `${numberWords[option.key]} Months`;
                    return (
                      <MenuItem key={option.key} value={option.value}>
                        {option.value} ({durationText})
                      </MenuItem>
                    );
                  })}
                </TextField>

                {/* Description */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Description
                  </Typography>
                  <Box sx={{ minHeight: 200 }}>
                    <CKEditor
                      editor={ClassicEditor}
                      data={editorData}
                      onChange={(event, editor) => {
                        const data = editor.getData();
                        setEditorData(data);
                        setCourse(prev => ({ ...prev, description: data }));
                      }}
                      config={{
                        toolbar: ['bold', 'italic', 'bulletedList', 'numberedList', 'link']
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Files & Contact */}
        <Grid item xs={12} md={6}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* File Upload Card */}
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <UploadIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Files & Media
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      Upload Course File
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Upload information about your course/class for users to download.
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Accept: PDF, Word Doc.
                    </Typography>
                    {course.file ? (
                      <Box sx={{ 
                        border: `1px solid ${theme.palette.primary.main}`,
                        borderRadius: 1,
                        p: 2,
                        mt: 1,
                        backgroundColor: theme.palette.primary.main + '05'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {typeof course.file === 'string' ? (
                              // Existing file from API (URL string)
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontWeight: 500,
                                  color: 'primary.main',
                                  textDecoration: 'underline',
                                  cursor: 'pointer'
                                }}
                                onClick={() => window.open(course.file, '_blank')}
                              >
                                📄 View Course File
                              </Typography>
                            ) : (
                              // New file upload (File object)
                              <>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  📄 {course.file.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  ({(course.file.size / 1024 / 1024).toFixed(2)} MB)
                                </Typography>
                              </>
                            )}
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => setCourse(prev => ({ ...prev, file: null }))}
                            sx={{ 
                              color: '#000',
                              '&:hover': {
                                backgroundColor: 'rgba(0,0,0,0.1)'
                              }
                            }}
                          >
                            <CloseIcon />
                          </IconButton>
                        </Box>
                      </Box>
                    ) : (
                      <Button
                        variant="outlined"
                        component="label"
                        fullWidth
                        sx={{ mt: 1 }}
                      >
                        Choose file
                        <input
                          type="file"
                          hidden
                          accept=".pdf,.doc,.docx"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              setCourse(prev => ({ ...prev, file: file }));
                            }
                          }}
                        />
                      </Button>
                    )}
                  </Box>

                  <Divider />

                  <Box>
                    {/* Use the proper UploadCourseImage component */}
                    <UploadCourseImage
                      course={course}
                      setCourse={setCourse}
                      selectedImages={selectedImages}
                      setSelectedImages={setSelectedImages}
                      error={error}
                      imageDisplayData={imageDisplayData}
                      setImageDisplayData={setImageDisplayData}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Contact & Settings Card */}
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ContactIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Contact & Settings
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Website */}
                  <TextField
                    fullWidth
                    label="Website (optional)"
                    value={course.socialLink}
                    onChange={(e) => setCourse(prev => ({ ...prev, socialLink: e.target.value }))}
                  />

                  {/* Course by */}
                  <TextField
                    fullWidth
                    label="Course by (optional)"
                    value={course.by}
                    onChange={(e) => setCourse(prev => ({ ...prev, by: e.target.value }))}
                  />

                    {/* Contact Methods */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Contact Methods
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Select how potential clients can contact you. You must select at least one method.
                      </Typography>
                      
                      <FormControl component="fieldset" error={!!error.contactSources}>
                        <FormGroup>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={course.contactSources.includes("email")}
                                onChange={handleContactCheckboxChange}
                                value="email"
                              />
                            }
                            label="Email"
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={course.contactSources.includes("phone")}
                                onChange={handleContactCheckboxChange}
                                value="phone"
                              />
                            }
                            label="Phone"
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={course.contactSources.includes("our-message-system")}
                                onChange={handleContactCheckboxChange}
                                value="our-message-system"
                              />
                            }
                            label="Our message system"
                          />
                        </FormGroup>
                        {error.contactSources && (
                          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                            {error.contactSources}
                          </Typography>
                        )}
                      </FormControl>

                    {/* Contact Details */}
                    {course.contactSources.includes("email") && (
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={course.email}
                        onChange={(e) => setCourse(prev => ({ ...prev, email: e.target.value }))}
                        error={!!error.email}
                        helperText={error.email}
                        sx={{ mt: 2 }}
                      />
                    )}

                    {course.contactSources.includes("phone") && (
                      <MuiTelInput
                        fullWidth
                        label="Phone Number"
                        value={course.telephone}
                        onChange={(value) => setCourse(prev => ({ ...prev, telephone: value }))}
                        defaultCountry="GB"
                        error={!!error.telephone}
                        helperText={error.telephone}
                        sx={{ mt: 2 }}
                      />
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>

      {/* Submit Button */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        mt: 3,
        pt: 2,
        borderTop: 1,
        borderColor: 'divider'
      }}>
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={loading}
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            borderRadius: 2,
            px: 6,
            py: 1.5,
            fontWeight: 700,
            fontSize: '1.1rem',
            textTransform: 'none',
            boxShadow: `0 4px 16px ${theme.palette.primary.main}30`,
            '&:active': {
              background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
              boxShadow: `0 2px 8px ${theme.palette.primary.main}50`,
              transform: 'translateY(1px)'
            },
            '&:hover': {
              background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
              boxShadow: `0 6px 20px ${theme.palette.primary.main}40`,
              transform: 'translateY(-1px)'
            },
            '&:disabled': {
              background: `linear-gradient(135deg, ${theme.palette.grey[400]} 0%, ${theme.palette.grey[500]} 100%)`,
              color: theme.palette.grey[600],
              boxShadow: 'none',
              transform: 'none'
            }
          }}
        >
          {loading ? (isEditMode ? 'Updating Course...' : 'Creating Course...') : (isEditMode ? 'Update Course' : 'Submit Course')}
        </Button>
      </Box>

      {/* Payment Modal */}
      <Modal
        open={isPaymentModalOpen}
        onClose={handlePaymentModalClose}
        aria-labelledby="payment-modal-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000, // Higher than navbar and dashboard menu
          '&:focus': {
            outline: 'none'
          }
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 0,
            overflow: 'auto',
          }}
        >
          {/* Close Button */}
          <Button
            onClick={handlePaymentModalClose}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 1,
              minWidth: 'auto',
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: 'rgba(0,0,0,0.1)',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.2)',
              }
            }}
          >
            ×
          </Button>

          {/* Payment Content */}
          <Elements stripe={stripePromise}>
            <CourseBilling
              advancePrice={course.advPrice}
              duration={course.duration}
              onPaymentSuccess={isEditMode ? updateCourseAfterPayment : handlePaymentSuccess}
              onPaymentFailure={handlePaymentFailure}
            />
          </Elements>
        </Box>
      </Modal>
    </Box>
  );
};

export default CreateCourseForm;
